import React from 'react';
import {Card, DonutChart} from './primitives';
import {
    OverallRiskIcon,
    CardiacRiskIcon,
    DiabeticRiskIcon,
    ComorbidityRiskIcon,
} from './RiskIcons';
import type {
    OhdBloodPressureBreakdown,
    OhdHba1cBreakdown,
    OhdParticipation,
    OhdRiskMetrics,
} from '../types';
import {DONUT_TRACK, RISK_TILE_COLORS} from './chart-colors';

interface Props {
    risk: OhdRiskMetrics;
    participation: OhdParticipation;
    bp: OhdBloodPressureBreakdown;
    hba1c: OhdHba1cBreakdown;
}

interface RiskTile {
    label: string;
    count: number | undefined;
    icon: React.ReactNode;
    color: string;
}

function pctOf(num: number, denom: number): number {
    if (!denom) return 0;
    return Math.round((num / denom) * 100);
}

const RiskAssessmentCard: React.FC<Props> = ({risk, participation, bp, hba1c}) => {
    const total = participation.total_participants || 1;

    // Overall and Comorbidity are optional — render as "pending" until the OHD API ships them.
    const tiles: RiskTile[] = [
        {label: 'Overall Risk', count: risk.overall_at_risk, icon: <OverallRiskIcon />, color: RISK_TILE_COLORS.overall},
        {label: 'Cardiac Risk', count: risk.cardiac_at_risk, icon: <CardiacRiskIcon />, color: RISK_TILE_COLORS.cardiac},
        {label: 'Diabetic Risk', count: risk.diabetic_at_risk, icon: <DiabeticRiskIcon />, color: RISK_TILE_COLORS.diabetic},
        {label: 'Comorbidity Risk', count: risk.comorbidity_at_risk, icon: <ComorbidityRiskIcon />, color: RISK_TILE_COLORS.comorbidity},
    ];

    const bpOutOfRange = bp.elevated + bp.high_stage_1 + bp.high_stage_2 + bp.hypertensive_crisis;
    const hba1cOutOfRange = hba1c.pre_diabetic + hba1c.diabetic;

    const outOfRange = [
        {label: 'Blood Pressure', count: bpOutOfRange, pct: pctOf(bpOutOfRange, bp.total)},
        {label: 'Blood Glucose', count: hba1cOutOfRange, pct: pctOf(hba1cOutOfRange, hba1c.total)},
    ];

    return (
        <div>
            <h2 className="mb-2 text-[24px] text-mld-muted-text">Risk Assessment</h2>
            <Card>
                <div className="flex divide-x divide-mld-panel-border">
                    {tiles.map((t) => {
                        const isPending = typeof t.count !== 'number';
                        const count = isPending ? 0 : (t.count as number);
                        const pct = pctOf(count, total);
                        return (
                            <div
                                key={t.label}
                                className={`flex flex-1 flex-col items-center px-1 py-3${isPending ? ' opacity-40' : ''}`}>
                                <div className="mb-4 flex items-center gap-1 text-[14px] text-mld-muted-text">
                                    {t.label}
                                    <span className="text-mld-muted-text cursor-help" title={isPending ? 'Pending API metric' : 'Info'}>
                                        &#9432;
                                    </span>
                                </div>
                                <DonutChart
                                    pct={isPending ? 0 : pct}
                                    size={130}
                                    strokeWidth={8}
                                    color={t.color}
                                    trackColor={DONUT_TRACK}>
                                    <div className="-mt-1 -mb-1">{t.icon}</div>
                                    {isPending ? (
                                        <div className="text-[14px] text-mld-muted-text">n/a</div>
                                    ) : (
                                        <div className="text-[32px] font-semibold text-mld-text-primary">{pct}%</div>
                                    )}
                                </DonutChart>
                                {isPending ? (
                                    <div className="mt-2 text-[12px] text-mld-muted-text">pending</div>
                                ) : (
                                    <>
                                        <div className="mt-2 text-[28px] leading-tight font-medium text-mld-text-primary">
                                            {count.toLocaleString()}
                                        </div>
                                        <div className="-mt-0.5 text-[12px] text-mld-muted-text">Participants</div>
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="flex w-[180px] shrink-0 flex-col px-4 py-3">
                        <div className="text-[14px] text-mld-muted-text">Out-of-Range Analysis</div>
                        <div className="divide-y divide-mld-panel-border">
                            {outOfRange.map((o) => (
                                <div key={o.label} className="py-1.5 text-center">
                                    <div className="text-[12px] text-mld-muted-text">{o.label}</div>
                                    <div className="-mt-1 text-[18px] text-mld-muted-text">
                                        {o.count.toLocaleString()} ({o.pct}%)
                                    </div>
                                </div>
                            ))}
                            {/* BMI and Lipids rows hidden — not in current OHD response */}
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default RiskAssessmentCard;
